import Layout from '@/components/feature/Layout';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

const faqs = [
  {
    question: '凤楼信息是一个什么样的平台？',
    answer: '凤楼信息是一个专注于本地化信息服务的综合平台，致力于为用户提供最全面、最及时的凤楼区域资讯。我们的内容涵盖本地生活、商务资讯、休闲娱乐、美食探店、旅游出行、科技数码等多个领域，帮助用户高效获取所需信息。',
  },
  {
    question: '凤楼信息的内容来源是什么？',
    answer: '我们的内容由专业的编辑团队进行采编和审核，同时整合了来自权威渠道的公开信息。我们始终秉持客观、真实、及时的原则，确保每一条信息的准确性和可靠性。部分内容来自行业专家和资深从业者的投稿分享。',
  },
  {
    question: '如何订阅凤楼信息的最新资讯？',
    answer: '您可以在网站首页底部的订阅区域输入您的邮箱地址，点击"立即订阅"即可。订阅后，我们将定期向您推送最新的凤楼区域资讯和精选内容。您的邮箱地址将严格保密，不会泄露给任何第三方。',
  },
  {
    question: '凤楼信息的内容是免费的吗？',
    answer: '是的，凤楼信息目前所有内容均免费向用户开放。我们希望通过免费高质量的信息服务，帮助更多人了解凤楼区域的发展动态和生活资讯。未来我们可能会推出一些付费增值服务，但基础内容将始终保持免费。',
  },
  {
    question: '我可以在凤楼信息上投稿吗？',
    answer: '我们欢迎优质内容创作者加入！如果您对凤楼区域有深入了解，或在某个领域有专业知识，欢迎通过我们的联系方式提交稿件。稿件经编辑审核通过后将会发布，并注明作者信息。具体要求请发送邮件至我们的编辑团队。',
  },
  {
    question: '遇到网站使用问题如何联系技术支持？',
    answer: '如果您在使用过程中遇到任何问题，可以通过以下方式联系我们：发送邮件至 contact@fenglou-info.com，或拨打客服热线 400-888-9999。我们的客服团队工作时间为周一至周五 9:00-18:00，节假日除外。',
  },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 凤楼信息',
  description: '凤楼信息品牌介绍，了解我们的服务理念、用户口碑和联系方式。最全面、最及时的本地信息服务平台。',
  url: 'https://www.fenglou-info.com/about',
  mainEntity: {
    '@type': 'Organization',
    name: '凤楼信息',
    url: 'https://www.fenglou-info.com',
    description: '凤楼信息为您提供最全面、最及时的凤楼信息服务，涵盖本地生活、商务资讯、休闲娱乐等多维度内容。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-9999',
      contactType: 'customer service',
      email: 'contact@fenglou-info.com',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '信息大道168号',
      addressLocality: '凤楼区',
      addressCountry: 'CN',
    },
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  return (
    <Layout>
      <SEOMeta
        title="关于我们 - 凤楼信息"
        description="凤楼信息品牌介绍，了解我们的服务理念、用户口碑和联系方式。最全面、最及时的本地信息服务平台。"
        keywords="凤楼信息,关于我们,品牌介绍,凤楼口碑,凤楼联系方式,信息服务平台"
        canonicalUrl="https://www.fenglou-info.com/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="pt-24 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          {/* Brand Intro */}
          <section className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
                  品牌介绍
                </span>
                <h1
                  className="text-3xl md:text-4xl font-bold text-foreground-900 leading-tight"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  关于凤楼信息
                </h1>
                <p className="text-foreground-600 mt-6 leading-relaxed">
                  凤楼信息成立于2024年，是凤楼区域领先的综合信息服务平台。我们致力于为本地居民和关注凤楼区域发展的朋友提供最全面、最及时、最可靠的资讯服务。
                </p>
                <p className="text-foreground-600 mt-3 leading-relaxed">
                  我们的团队由资深的媒体人、行业分析师和技术专家组成，以专业眼光和严谨态度，深入挖掘凤楼区域的每一个精彩故事。从城市发展规划到社区生活日常，从商业投资机会到周末出游攻略，凤楼信息始终陪伴在您身边。
                </p>

                <div className="grid grid-cols-3 gap-6 mt-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-label)' }}>
                      12+
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">内容板块</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-label)' }}>
                      500+
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">原创文章</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-500" style={{ fontFamily: 'var(--font-label)' }}>
                      50万+
                    </div>
                    <div className="text-xs text-foreground-500 mt-1">月均阅读</div>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20team%20collaboration%2C%20diverse%20professionals%20working%20together%2C%20bright%20open%20workspace%2C%20natural%20light%2C%20warm%20atmosphere%2C%20creative%20environment%2C%20professional%20editorial%20photography%2C%20clean%20aesthetic&width=800&height=600&seq=about-brand-fenglou&orientation=landscape"
                  alt="凤楼信息团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="mt-20">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3 whitespace-nowrap">
                核心价值
              </span>
              <h2
                className="text-2xl md:text-3xl font-bold text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                我们的使命
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-star-line',
                  title: '真实可靠',
                  desc: '严格的内容审核机制，确保每一条信息的真实性和准确性。我们坚信，真实是信息的生命。',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '及时高效',
                  desc: '专业编辑团队全天候跟踪本地动态，第一时间为您推送重要资讯，让您永远快人一步。',
                },
                {
                  icon: 'ri-heart-line',
                  title: '服务为本',
                  desc: '以用户需求为核心，不断优化内容质量和阅读体验，做凤楼区域居民最信赖的信息伙伴。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center mx-auto rounded-2xl bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mt-20">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-3 whitespace-nowrap">
                用户口碑
              </span>
              <h2
                className="text-2xl md:text-3xl font-bold text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                用户怎么说
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${i < t.rating ? 'text-accent-500' : 'text-background-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mt-20">
            <div className="bg-background-100 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
                    联系我们
                  </span>
                  <h2
                    className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    期待与您沟通
                  </h2>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-6">
                    无论您有投稿意向、商务合作需求，还是使用中遇到问题，我们都诚挚地欢迎您与我们联系。
                  </p>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <div className="text-foreground-400 text-xs">电子邮箱</div>
                        <div className="text-foreground-800 font-medium">contact@fenglou-info.com</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <div className="text-foreground-400 text-xs">客服热线</div>
                        <div className="text-foreground-800 font-medium">400-888-9999</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <div className="text-foreground-400 text-xs">公司地址</div>
                        <div className="text-foreground-800 font-medium">凤楼区信息大道168号</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <div className="text-foreground-400 text-xs">工作时间</div>
                        <div className="text-foreground-800 font-medium">周一至周五 9:00 - 18:00</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3] lg:aspect-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20office%20building%20exterior%20in%20Chinese%20city%2C%20glass%20facade%2C%20green%20trees%2C%20professional%20architecture%2C%20bright%20daylight%2C%20corporate%20headquarters%2C%20clean%20minimal%20design%2C%20warm%20atmosphere&width=800&height=600&seq=about-contact-fenglou&orientation=landscape"
                    alt="凤楼信息总部"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-20">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-3 whitespace-nowrap">
                常见问题
              </span>
              <h2
                className="text-2xl md:text-3xl font-bold text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                您可能想了解
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <details key={index} className="group border-b border-background-200 last:border-b-0">
                  <summary className="flex items-center justify-between py-4 cursor-pointer list-none">
                    <h3 className="text-base font-medium text-foreground-800 pr-4">{faq.question}</h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </summary>
                  <div className="pb-4 text-sm text-foreground-500 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}